import React, { useCallback, useMemo } from 'react';

import { link } from '@aline/routes';
import { LinearProgress, Typography } from '@mui/material';
import { ChevronRight } from 'lucide-react';
import { matchSorter } from 'match-sorter';
import { AlineTooltip, Spacer } from 'src/components';
import {
  getIsJobSyncing,
  getJobTotalFileIds,
} from 'src/components/admin/integrations/FolderSyncJobsList';
import {
  getServiceIdFromIntegration,
  IntegrationDisplay,
} from 'src/components/admin/integrations/IntegrationRow';
import { SyncingIndicator } from 'src/components/admin/integrations/SyncingIndicator';
import { useFolderSyncJobs } from 'src/components/admin/integrations/useFolderSyncJobs';
import { ErrorIcon } from 'src/components/icons/ErrorIcon';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers/url';
import { useFileStorageConnections, useOnFolderSyncJobUpdateSubscription } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  clickableIntegrationRow: {
    '& .MuiTypography-subtitle2': { fontSize: 14, fontWeight: 400 },
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    alignItems: 'center',
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 1),
  },
  icon: {
    display: 'block',
    height: 20,
    width: 20,
  },
  // The name stays on one line; the sync status beside it gives up the space instead.
  integrationName: {
    '& .MuiTypography-subtitle2': { whiteSpace: 'nowrap' },
    flexShrink: 0,
  },
  listContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 0,
    overflow: 'auto',
  },
  noResults: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
  },
  progressBar: {
    borderRadius: 4,
    flexShrink: 1,
    height: 4,
    minWidth: 24,
    width: 80,
  },
  syncingStatus: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  syncingText: {
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
}));

export const autoSyncParamKey = 'autoSync';

/** Compact integrations section used inside the editor's Add menu. */
export const ChatIntegrationsMenu = React.memo(function ChatIntegrationsMenu({
  onClose,
  query,
}: {
  onClose: () => void;
  query: string;
}): JSX.Element | null {
  const { classes } = useStyles();
  const { fileStorageConnections, initialLoading } = useFileStorageConnections({
    fetchPolicy: 'cache-first',
  });
  useOnFolderSyncJobUpdateSubscription();
  // Connected integrations first.
  const integrations = useMemo(() => {
    const matches = query.trim()
      ? matchSorter(fileStorageConnections, query, { keys: ['name'] })
      : fileStorageConnections;
    return [...matches].sort(
      (a, b) =>
        Number(b.state === Apollo.ApiDeckConnectionState.Callable) -
        Number(a.state === Apollo.ApiDeckConnectionState.Callable)
    );
  }, [fileStorageConnections, query]);
  const openIntegration = useCallback(
    (integration: Apollo.FileStorageIntegration) => {
      onClose();
      openIntegrationsPage(integration);
    },
    [onClose]
  );
  if (initialLoading) return null;

  if (!integrations.length) {
    return (
      <div className={classes.listContainer}>
        <Typography className={classes.noResults} variant="body2">
          No integrations found
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.listContainer}>
      {integrations.map(integration => (
        <IntegrationRowWithStatus
          key={integration.id}
          integration={integration}
          onSyncClick={() => openIntegration(integration.serviceId)}
        />
      ))}
    </div>
  );
});

interface IntegrationRowWithStatusProps {
  integration: Apollo.FileStorageConnection;
  onSyncClick: () => void;
}

function IntegrationRowWithStatus({
  integration,
  onSyncClick,
}: IntegrationRowWithStatusProps): JSX.Element {
  // Only connected integrations fetch their sync jobs.
  if (integration.state !== Apollo.ApiDeckConnectionState.Callable) {
    return (
      <ClickableIntegrationRow integration={integration} onClick={onSyncClick}>
        <ChevronRight size={16} />
      </ClickableIntegrationRow>
    );
  }

  return <ConnectedIntegrationRow integration={integration} onOpen={onSyncClick} />;
}

interface ConnectedIntegrationRowProps {
  integration: Apollo.FileStorageConnection;
  /** Closes the menu, then opens this integration on the integrations page. */
  onOpen: () => void;
}

function ConnectedIntegrationRow({
  integration,
  onOpen,
}: ConnectedIntegrationRowProps): JSX.Element {
  const { classes } = useStyles();
  const { grouped, hasValues, loading } = useFolderSyncJobs(integration.serviceId);

  // Sync status across all of this integration's jobs.
  const { failedFilesCount, isAnySyncing, syncedFiles, totalFiles } = useMemo(() => {
    const status = { failedFilesCount: 0, isAnySyncing: false, syncedFiles: 0, totalFiles: 0 };
    if (loading || !hasValues) return status;

    for (const { jobs } of Object.values(grouped)) {
      for (const job of jobs) {
        status.totalFiles += getJobTotalFileIds(job).length;
        status.syncedFiles += job.completedIds.length;
        status.failedFilesCount += job.failedFiles.filter(file => !file.isDimissed).length;
        if (getIsJobSyncing(job)) status.isAnySyncing = true;
      }
    }

    return status;
  }, [grouped, hasValues, loading]);
  const progressPercentage = totalFiles > 0 ? (syncedFiles / totalFiles) * 100 : 100;

  return (
    <ClickableIntegrationRow
      integration={integration}
      statusIndicator={isAnySyncing && <SyncingIndicator />}
      onClick={onOpen}
    >
      {failedFilesCount > 0 && (
        <AlineTooltip arrow placement="top" title={`${failedFilesCount} files failed to sync`}>
          <ErrorIcon style={{ height: 20, width: 20 }} />
        </AlineTooltip>
      )}
      <div className={classes.syncingStatus}>
        <Typography className={classes.syncingText} color="textSecondary" variant="body2">
          {syncedFiles}/{totalFiles} files synced
        </Typography>
        <LinearProgress
          className={classes.progressBar}
          value={progressPercentage}
          variant="determinate"
        />
      </div>
    </ClickableIntegrationRow>
  );
}

interface ClickableIntegrationRowProps {
  children: React.ReactNode;
  integration: Apollo.FileStorageConnection;
  onClick: () => void;
  /** Status indicator rendered after the integration name */
  statusIndicator?: React.ReactNode;
}

function ClickableIntegrationRow({
  children,
  integration,
  onClick,
  statusIndicator,
}: ClickableIntegrationRowProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <div
      className={classes.clickableIntegrationRow}
      role="button"
      tabIndex={0}
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      onMouseDown={event => event.preventDefault()}
    >
      <IntegrationDisplay
        className={classes.integrationName}
        iconClassName={classes.icon}
        imageUrl={integration.imageUrl}
        isConnected={integration.state === Apollo.ApiDeckConnectionState.Callable}
        name={integration.name}
      />
      {statusIndicator}
      <Spacer />
      {children}
    </div>
  );
}

/**
 * Opens the integrations page on one integration in a new Aline tab, so the chat the user was
 * composing in stays put. An already open Integrations tab is reused, and the route is applied
 * to it so the page still receives the `autoSync` param.
 */
function openIntegrationsPage(integration: Apollo.FileStorageIntegration): void {
  openLink(
    `${link.AdminIntegrations()}?${autoSyncParamKey}=${getServiceIdFromIntegration(integration)}`,
    { applyRouteToExistingTab: true }
  );
}
